import React, { useState, useMemo, useEffect, useCallback, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    Header,
    Button,
    Message,
    Icon,
    Accordion,
    Table,
    Dropdown,
} from 'semantic-ui-react';
import { PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { LoadingWrapper } from '../common';
import { useRoadblockReport } from './hooks';
import { State } from 'country-state-city';
import { typeChartColor, OPERATING_STATE_CODES } from './constants';
import { roadblockTypePillStyle } from '../Forms/Roadblock/constants';

const getStateName = (code) => State.getStateByCodeAndCountry(code, 'US')?.name || code;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatHours = (val, decimals = 1) => {
    if (val === null || val === undefined) return '—';
    return `${parseFloat(Number(val).toFixed(decimals))}h`;
};

const STATE_OPTIONS = [
    { key: '', value: '', text: 'All States' },
    ...OPERATING_STATE_CODES.map(code => ({
        key: code, value: code, text: getStateName(code),
    })),
];

// ─── DateInput ───────────────────────────────────────────────────────────────

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <div
        ref={ref}
        onClick={onClick}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            background: '#fff',
            minWidth: '130px',
            fontSize: '0.95rem',
            userSelect: 'none',
        }}
    >
        <span style={{ flex: 1, color: value ? '#222' : '#999' }}>
            {value || placeholder}
        </span>
        <Icon name="calendar outline" style={{ margin: 0, color: '#aaa' }} />
    </div>
));

// ─── SummaryCard ─────────────────────────────────────────────────────────────

const SummaryCard = ({ title, value, subtitle }) => (
    <div style={{
        flex: '1 1 160px',
        background: '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        minWidth: '140px',
    }}>
        <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: '0.3rem' }}>{title}</div>
        <div style={{ fontSize: '1.9rem', fontWeight: '700', lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>{subtitle}</div>
    </div>
);

// ─── AvgResolutionTable ───────────────────────────────────────────────────────

const AvgResolutionTable = ({ byType, avgHoursOverall }) => (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: '700', fontSize: '1rem' }}>Average time to Resolve</span>
            {avgHoursOverall != null && (
                <span style={{ fontSize: '0.82rem', color: '#555' }}>
                    Overall Avg.&nbsp;
                    <strong>{formatHours(avgHoursOverall)}</strong>
                </span>
            )}
        </div>
        <Table basic="very" compact unstackable style={{ fontSize: '0.88rem' }}>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Total</Table.HeaderCell>
                    <Table.HeaderCell>Open</Table.HeaderCell>
                    <Table.HeaderCell>Avg. hrs</Table.HeaderCell>
                    <Table.HeaderCell>VS Overall</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {(byType || []).map((row) => {
                    const hrs = row.avgResolutionDays ?? null;
                    const diff = hrs != null && avgHoursOverall != null
                        ? hrs - avgHoursOverall
                        : null;
                    return (
                        <Table.Row key={row.typeId}>
                            <Table.Cell>
                                <span style={roadblockTypePillStyle(row.typeId)}>
                                    {row.label}
                                </span>
                            </Table.Cell>
                            <Table.Cell>{row.total ?? '—'}</Table.Cell>
                            <Table.Cell>{row.open ?? '—'}</Table.Cell>
                            <Table.Cell>{formatHours(hrs)}</Table.Cell>
                            <Table.Cell>
                                {diff === null ? '—' : (
                                    <span style={{ color: diff > 0 ? '#e53935' : '#43a047', fontWeight: 600 }}>
                                        {diff > 0 ? '+' : ''}{diff.toFixed(1)}h
                                    </span>
                                )}
                            </Table.Cell>
                        </Table.Row>
                    );
                })}
            </Table.Body>
        </Table>
    </div>
);

// ─── TypeDonutChart ───────────────────────────────────────────────────────────

const TypeDonutChart = ({ byType }) => {
    const chartData = useMemo(() => (byType || []).map(r => ({
        name: r.label,
        typeId: r.typeId,
        value: r.total ?? 0,
    })), [byType]);

    const chartTotal = chartData.reduce((acc, d) => acc + d.value, 0);

    return (
        <div>
            <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.75rem' }}>
                Roadblock Types
            </div>
            <div className="rb-chart-flex" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
                    <PieChart
                        key={chartData.map(d => `${d.typeId}:${d.value}`).join('|')}
                        width={180}
                        height={180}
                    >
                        <Pie
                            data={chartData}
                            cx={85}
                            cy={85}
                            innerRadius={55}
                            outerRadius={80}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={2}
                        >
                            {chartData.map((entry) => (
                                <Cell
                                    key={entry.typeId}
                                    fill={typeChartColor(entry.typeId)}
                                />
                            ))}
                        </Pie>
                    </PieChart>
                    {/* Center label */}
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        pointerEvents: 'none',
                    }}>
                        <div style={{ fontSize: '1.6rem', fontWeight: '700', lineHeight: 1 }}>
                            {chartTotal}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#888' }}>Total</div>
                    </div>
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {chartData.map((entry) => {
                        const pct = chartTotal > 0 ? Math.round((entry.value / chartTotal) * 100) : 0;
                        return (
                            <div key={entry.typeId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: typeChartColor(entry.typeId),
                                    flexShrink: 0,
                                }} />
                                <span>{entry.name} – {entry.value} ({pct}%)</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ─── BreakdownTable ───────────────────────────────────────────────────────────

const BreakdownTable = ({ rows, isTeamLevel, onRowClick }) => (
    <div style={{ overflowX: 'auto' }}>
    <Table basic="very" compact unstackable style={{ fontSize: '0.88rem' }}>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Total</Table.HeaderCell>
                <Table.HeaderCell>Open</Table.HeaderCell>
                {!isTeamLevel && <Table.HeaderCell>Resolved</Table.HeaderCell>}
                <Table.HeaderCell>Prod. Impact</Table.HeaderCell>
                <Table.HeaderCell>Avg. Time</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {rows.map((row, idx) => (
                <Table.Row
                    key={row.name || row.id || idx}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : {}}
                    className={onRowClick ? 'breakdown-row-clickable' : ''}
                >
                    <Table.Cell style={{ fontWeight: 500 }}>{row.displayName || row.name}</Table.Cell>
                    <Table.Cell>{row.total ?? '—'}</Table.Cell>
                    <Table.Cell style={{ color: (row.open > 0) ? '#2185d0' : undefined }}>
                        {row.open ?? '—'}
                    </Table.Cell>
                    {!isTeamLevel && (
                        <Table.Cell>{row.resolved ?? '—'}</Table.Cell>
                    )}
                    <Table.Cell>{row.productionImpacting ?? '—'}</Table.Cell>
                    <Table.Cell>{formatHours(row.avgResolutionDays ?? null)}</Table.Cell>
                </Table.Row>
            ))}
        </Table.Body>
    </Table>
    </div>
);

// ─── MarketByStateAccordion ───────────────────────────────────────────────────

const MarketByStateAccordion = ({ marketsByState, onMarketClick }) => {
    const [openStates, setOpenStates] = useState(() => {
        if (marketsByState?.length > 0) return new Set([marketsByState[0].stateCode]);
        return new Set();
    });

    // Reset when data changes
    useEffect(() => {
        if (marketsByState?.length > 0) {
            setOpenStates(new Set([marketsByState[0].stateCode]));
        }
    }, [marketsByState]);

    const toggle = (code) => setOpenStates(prev => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return next;
    });

    return (
        <Accordion styled fluid>
            {marketsByState.map((group) => (
                <React.Fragment key={group.stateCode}>
                    <Accordion.Title
                        active={openStates.has(group.stateCode)}
                        onClick={() => toggle(group.stateCode)}
                        style={{ background: '#f5f5f5', fontWeight: 600 }}
                    >
                        <Icon name="dropdown" />
                        {group.stateName}
                    </Accordion.Title>
                    <Accordion.Content active={openStates.has(group.stateCode)}>
                        <BreakdownTable
                            rows={group.markets}
                            isTeamLevel={false}
                            onRowClick={onMarketClick}
                        />
                    </Accordion.Content>
                </React.Fragment>
            ))}
        </Accordion>
    );
};

// ─── RoadblockReport ─────────────────────────────────────────────────────────

export const RoadblockReport = () => {
    const navigate = useNavigate();
    const {
        data,
        loaded,
        error,
        exporting,
        activeSummary,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        selectedState,
        setSelectedState,
        statusFilter,
        setStatusFilter,
        drilldown,
        handleDrillToTeam,
        handleResetDrilldown,
        handleExport,
    } = useRoadblockReport();

    const breakdown = data?.breakdown ?? [];
    const totalAll = (activeSummary.totalOpen ?? 0) + (activeSummary.totalClosedInRange ?? 0);
    const avgHoursOverall = activeSummary.avgResolutionDaysOverall ?? null;

    // Tracks whether the "Open – Last 30 Days" preset is active.
    // Cleared whenever the user changes dates or picks a different status pill.
    const [activePreset, setActivePreset] = useState(null);

    const handleOpenLast30Days = useCallback(() => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        setStatusFilter('open');
        setStartDate(thirtyDaysAgo);
        setEndDate(today);
        setActivePreset('open_30');
    }, [setStatusFilter, setStartDate, setEndDate]);

    const handleStateChange = useCallback((_, { value }) => {
        setSelectedState(value);
        handleResetDrilldown();
        setActivePreset(null);
    }, [handleResetDrilldown]);

    // Clears the state filter and any active drilldown (used by the back button)
    const clearStateFilter = useCallback(() => {
        setSelectedState('');
        handleResetDrilldown();
        setActivePreset(null);
    }, [handleResetDrilldown]);

    // Status-filtered breakdown rows
    const filteredBreakdown = useMemo(() => {
        if (statusFilter === 'open')   return breakdown.filter(r => (r.open ?? 0) > 0);
        if (statusFilter === 'closed') return breakdown.filter(r => (r.resolved ?? 0) > 0);
        return breakdown;
    }, [breakdown, statusFilter]);

    // Grouped by state for accordion (only when not drilling down and no specific state selected)
    const marketsByState = useMemo(() => {
        if (drilldown || selectedState) return null;
        const grouped = {};
        filteredBreakdown.forEach(row => {
            const code = row.stateCode || 'Unknown';
            if (!grouped[code]) {
                grouped[code] = {
                    stateCode: code,
                    stateName: row.stateName || getStateName(code),
                    markets: [],
                };
            }
            grouped[code].markets.push(row);
        });
        return Object.values(grouped);
    }, [filteredBreakdown, drilldown, selectedState]);

    // Breadcrumb / section title for breakdown
    const breakdownTitle = drilldown
        ? `Teams – ${drilldown.marketName}`
        : selectedState
            ? `Markets – ${getStateName(selectedState)}`
            : 'Market by State';

    // "View All" roadblock link for team drilldown
    const viewAllLink = drilldown
        ? `/forms/roadblock?state=${drilldown.stateCode}&market=${drilldown.market}`
        : null;

    return (
        <div style={{ padding: '1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
            {/* Mobile-responsive overrides */}
            <style>{`
                @media (max-width: 767px) {
                    .rb-report-header {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.5rem;
                    }
                    .rb-report-export {
                        position: static !important;
                        align-self: center;
                        order: -1;
                    }
                    .rb-chart-flex {
                        justify-content: center;
                    }
                }
            `}</style>
            <LoadingWrapper loading={!loaded}>
                {/* Page header */}
                <div className="rb-report-header" style={{ position: 'relative', textAlign: 'center', marginBottom: '1.25rem' }}>
                    <Header as="h2" style={{ marginBottom: '0.15rem' }}>
                        Roadblocks Report Dashboard
                    </Header>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        Overview of roadblocks volume, type distribution, and resolution performance.
                    </p>
                    <div className="rb-report-export" style={{ position: 'absolute', right: 0, top: 0 }}>
                        <Button
                            basic
                            icon
                            labelPosition="left"
                            onClick={handleExport}
                            loading={exporting}
                            disabled={exporting || !data}
                        >
                            <Icon name="file outline" />
                            Export Data
                        </Button>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}

                {/* Filter bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    marginBottom: '1.25rem',
                    padding: '0.75rem 1rem',
                    background: '#fafafa',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                }}>
                    {/* State selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Icon name="globe" style={{ color: '#555' }} />
                        <Dropdown
                            selection
                            search
                            placeholder="All States"
                            options={STATE_OPTIONS}
                            value={selectedState}
                            onChange={handleStateChange}
                            style={{ minWidth: '140px' }}
                        />
                    </div>

                    {/* Status pills */}
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        {[
                            { key: 'all',    label: 'All',    count: totalAll },
                            { key: 'open',   label: 'Open',   count: activeSummary.totalOpen ?? 0 },
                            { key: 'closed', label: 'Closed', count: activeSummary.totalClosedInRange ?? 0 },
                        ].map(({ key, label, count }) => {
                            const isActive = key === 'open'
                                ? statusFilter === 'open' && activePreset === null
                                : statusFilter === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => { setStatusFilter(key); setActivePreset(null); }}
                                    style={{
                                        padding: '0.3em 0.75em',
                                        borderRadius: '20px',
                                        border: isActive ? '1px solid #222' : '1px solid #ccc',
                                        background: isActive ? '#222' : '#fff',
                                        color: isActive ? '#fff' : '#555',
                                        cursor: 'pointer',
                                        fontWeight: isActive ? 700 : 500,
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    {label} {count}
                                </button>
                            );
                        })}
                        <button
                            onClick={handleOpenLast30Days}
                            style={{
                                padding: '0.3em 0.75em',
                                borderRadius: '20px',
                                border: activePreset === 'open_30' ? '1px solid #2185d0' : '1px solid #ccc',
                                background: activePreset === 'open_30' ? '#2185d0' : '#fff',
                                color: activePreset === 'open_30' ? '#fff' : '#555',
                                cursor: 'pointer',
                                fontWeight: activePreset === 'open_30' ? 700 : 500,
                                fontSize: '0.85rem',
                            }}
                        >
                            Open – Last 30 Days
                        </button>
                    </div>

                    {/* Date pickers */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.85rem', color: '#555' }}>Start Date</span>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => { setStartDate(date); setActivePreset(null); }}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            dateFormat="MM/dd/yyyy"
                            customInput={<DateInput placeholder="Start date" />}
                        />
                        <span style={{ fontSize: '0.85rem', color: '#555' }}>End Date</span>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => { setEndDate(date); setActivePreset(null); }}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            dateFormat="MM/dd/yyyy"
                            customInput={<DateInput placeholder="End date" />}
                        />
                    </div>
                </div>

                {data && (
                    <>
                        {/* Summary cards */}
                        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                            <SummaryCard
                                title="Total Roadblocks"
                                value={totalAll}
                                subtitle="All affecting deliveries"
                            />
                            <SummaryCard
                                title="Open Roadblocks"
                                value={activeSummary.totalOpen ?? 0}
                                subtitle="Open and affecting deliveries"
                            />
                            <SummaryCard
                                title="Production-Impacting"
                                value={activeSummary.totalProductionImpacting ?? 0}
                                subtitle="Affecting critical service delivery"
                            />
                            <SummaryCard
                                title="Production Hours Impacted"
                                value={formatHours(activeSummary.totalProductionHoursImpacted ?? 0, 2)}
                                subtitle="Total across all open roadblocks"
                            />
                            <SummaryCard
                                title="Days Impacted"
                                value={activeSummary.daysImpacted ?? 0}
                                subtitle="Unique work days impacted"
                            />
                        </div>

                        {/* Middle row: avg resolution + donut chart */}
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                            {/* Avg resolution table */}
                            <div style={{
                                flex: '1 1 320px',
                                background: '#fff',
                                border: '1px solid #e8e8e8',
                                borderRadius: '8px',
                                padding: '1rem 1.25rem',
                            }}>
                                <AvgResolutionTable
                                    byType={activeSummary.byType}
                                    avgHoursOverall={avgHoursOverall}
                                />
                            </div>

                            {/* Donut chart */}
                            <div style={{
                                flex: '1 1 280px',
                                background: '#fff',
                                border: '1px solid #e8e8e8',
                                borderRadius: '8px',
                                padding: '1rem 1.25rem',
                            }}>
                                <TypeDonutChart byType={activeSummary.byType} />
                            </div>
                        </div>

                        {/* Breakdown section */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    {(drilldown || selectedState) && (
                                        <button
                                            onClick={drilldown ? handleResetDrilldown : clearStateFilter}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#2185d0',
                                                padding: 0,
                                                fontSize: '0.88rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.2rem',
                                            }}
                                        >
                                            <Icon name="arrow left" />
                                            {drilldown ? `Back to Markets – ${getStateName(drilldown.stateCode)}` : 'All States'}
                                        </button>
                                    )}
                                    <Header as="h4" style={{ margin: 0 }}>{breakdownTitle}</Header>
                                </div>
                                {viewAllLink && (
                                    <Button
                                        basic
                                        size="small"
                                        onClick={() => navigate('/forms/roadblock')}
                                    >
                                        View All
                                    </Button>
                                )}
                            </div>

                            {/* Accordion (market by state) or flat table */}
                            {marketsByState ? (
                                marketsByState.length === 0 ? (
                                    <p style={{ color: '#888', fontSize: '0.9rem' }}>No data for the selected filters.</p>
                                ) : (
                                    <MarketByStateAccordion
                                        marketsByState={marketsByState}
                                        onMarketClick={handleDrillToTeam}
                                    />
                                )
                            ) : (
                                filteredBreakdown.length === 0 ? (
                                    <p style={{ color: '#888', fontSize: '0.9rem' }}>No data for the selected filters.</p>
                                ) : (
                                    <div style={{
                                        background: '#fff',
                                        border: '1px solid #e8e8e8',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                    }}>
                                        <BreakdownTable
                                            rows={filteredBreakdown}
                                            isTeamLevel={!!drilldown}
                                            onRowClick={!drilldown && selectedState ? handleDrillToTeam : undefined}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    </>
                )}
            </LoadingWrapper>
        </div>
    );
};
